import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  Alert,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  Checkbox,
  FormControlLabel,
  FormGroup,
  Typography,
  Card,
  CardContent,
  Step,
  Stepper,
  StepLabel,
  Chip,
} from '@mui/material';
import {
  ContentCopy,
  AccountTree,
  DataObject,
  CheckCircle,
  LocationOn,
  DirectionsCar,
  Work,
} from '@mui/icons-material';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { apiClient } from '../../services/api';
import { useScenarios } from '../../hooks/useScenarios';
import { ErrorBoundary } from '../ErrorBoundary';

// Schema for scenario inheritance form
const scenarioInheritanceSchema = z.object({
  scenario_name: z.string().min(1, 'Scenario name is required').max(255, 'Name must be less than 255 characters'),
  scenario_description: z.string().optional(),
  source_scenario_id: z.number().min(1, 'Source scenario is required'),
  inherit_locations: z.boolean().default(true),
  inherit_vehicles: z.boolean().default(false),
  inherit_jobs: z.boolean().default(true),
  planning_horizon_days: z.number().min(1).max(365).default(30),
});

type ScenarioInheritanceFormData = z.infer<typeof scenarioInheritanceSchema>;

interface ScenarioInheritanceDialogProps {
  open: boolean;
  onClose: () => void;
  projectId: number;
  onSuccess?: (result: any) => void;
}

const steps = [
  'Configure Scenario',
  'Select Inheritance',
  'Review & Create'
];

export const ScenarioInheritanceDialog: React.FC<ScenarioInheritanceDialogProps> = ({
  open,
  onClose,
  projectId,
  onSuccess,
}) => {
  const [activeStep, setActiveStep] = useState(0);
  const [creationResult, setCreationResult] = useState<any>(null);
  const [isCreating, setIsCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const { data: scenariosData = [], isLoading: scenariosLoading } = useScenarios(projectId);

  // Defensive array handling for scenarios
  const scenarios = React.useMemo(() => {
    if (Array.isArray(scenariosData)) {
      return scenariosData;
    } else if (scenariosData?.data && Array.isArray(scenariosData.data)) {
      return scenariosData.data;
    }
    return [];
  }, [scenariosData]);

  const {
    control,
    handleSubmit,
    reset,
    watch,
    formState: { errors }
  } = useForm<ScenarioInheritanceFormData>({
    resolver: zodResolver(scenarioInheritanceSchema),
    defaultValues: {
      scenario_name: '',
      scenario_description: '',
      source_scenario_id: 0,
      inherit_locations: true,
      inherit_vehicles: false,
      inherit_jobs: true,
      planning_horizon_days: 30,
    },
  });

  // Watch form values for the review step
  const formValues = watch();
  const selectedSourceScenario = scenarios.find(s => s.id === formValues.source_scenario_id);

  // Get inheritance types array
  const getInheritanceTypes = () => {
    const types: ('locations' | 'vehicles' | 'jobs')[] = [];
    if (formValues.inherit_locations) types.push('locations');
    if (formValues.inherit_vehicles) types.push('vehicles');
    if (formValues.inherit_jobs) types.push('jobs');
    return types;
  };

  const handleNext = () => {
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const handleReset = () => {
    setActiveStep(0);
    setCreationResult(null);
    setError(null);
    reset();
  };

  const onSubmit = async (data: ScenarioInheritanceFormData) => {
    if (activeStep !== 2) return; // Only submit on final step

    setIsCreating(true);
    setError(null);

    try {
      console.log('🎯 Creating scenario with inheritance:', data);

      const inheritanceTypes = getInheritanceTypes();
      
      if (inheritanceTypes.length === 0) {
        throw new Error('At least one data type must be inherited');
      }

      const result = await apiClient.createScenarioWithInheritance({
        scenario_name: data.scenario_name,
        scenario_description: data.scenario_description,
        project_id: projectId,
        source_scenario_id: data.source_scenario_id,
        inherit_data_types: inheritanceTypes,
        planning_horizon_days: data.planning_horizon_days,
      });

      console.log('✅ Scenario inheritance result:', result);
      
      // Validate result structure before proceeding
      if (!result?.scenario?.id || !result?.dataset?.id) {
        console.error('❌ Invalid inheritance result structure:', result);
        throw new Error(`Invalid result structure - missing scenario or dataset IDs. Got: ${JSON.stringify(result)}`);
      }

      // Validate inheritance result
      if (!result?.inheritance_result?.copied_counts) {
        console.warn('⚠️ Missing inheritance result details:', result);
        // Add default structure for display
        result.inheritance_result = {
          ...result.inheritance_result,
          copied_counts: { locations: 0, vehicles: 0, jobs: 0 },
          execution_time_ms: 0
        };
      }

      setCreationResult(result);
      setActiveStep(3); // Move to success step
      onSuccess?.(result);

    } catch (error) {
      console.error('❌ Failed to create scenario with inheritance:', error);
      setError(error instanceof Error ? error.message : 'Failed to create scenario');
    } finally {
      setIsCreating(false);
    }
  };

  const handleClose = () => {
    handleReset();
    onClose();
  };

  const getStepContent = (step: number) => {
    switch (step) {
      case 0:
        return (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            <Alert severity="info" icon={<AccountTree />}>
              <Typography variant="body2" fontWeight="600" gutterBottom>
                Scenario-Based Data Inheritance
              </Typography>
              <Typography variant="caption">
                Create a new scenario by inheriting data from an existing "base case" scenario. 
                Perfect for comparing different vehicle configurations while keeping locations and jobs constant.
              </Typography>
            </Alert>

            <Controller
              name="scenario_name"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Scenario Name"
                  fullWidth
                  required
                  error={!!errors.scenario_name}
                  helperText={errors.scenario_name?.message}
                  placeholder="e.g., Scenario-change-vehicles"
                />
              )}
            />

            <Controller
              name="scenario_description"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Description (Optional)"
                  fullWidth
                  multiline
                  rows={3}
                  placeholder="Describe what this scenario tests or optimizes..."
                />
              )}
            />

            <Controller
              name="planning_horizon_days"
              control={control}
              render={({ field: { onChange, value, ...field } }) => (
                <TextField
                  {...field}
                  value={value || ''}
                  onChange={(e) => {
                    const val = parseInt(e.target.value);
                    onChange(isNaN(val) ? 30 : val);
                  }}
                  label="Planning Horizon (Days)"
                  type="number"
                  fullWidth
                  error={!!errors.planning_horizon_days}
                  helperText={errors.planning_horizon_days?.message || 'Number of days to plan ahead'}
                  inputProps={{ min: 1, max: 365 }}
                />
              )}
            />
          </Box>
        );

      case 1:
        return (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            <Controller
              name="source_scenario_id"
              control={control}
              render={({ field }) => (
                <FormControl fullWidth required error={!!errors.source_scenario_id}>
                  <InputLabel>Source Scenario (Base Case)</InputLabel>
                  <Select
                    {...field}
                    label="Source Scenario (Base Case)"
                    disabled={scenariosLoading}
                  >
                    {scenarios.map((scenario) => (
                      <MenuItem key={scenario.id} value={scenario.id}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <AccountTree fontSize="small" />
                          <Box>
                            <Typography variant="body2">{scenario.name}</Typography>
                            <Typography variant="caption" color="text.secondary">
                              {scenario.description || 'No description'}
                            </Typography>
                          </Box>
                        </Box>
                      </MenuItem>
                    ))}
                  </Select>
                  {errors.source_scenario_id && (
                    <FormHelperText>{errors.source_scenario_id.message}</FormHelperText>
                  )}
                </FormControl>
              )}
            />

            <Typography variant="h6" sx={{ mt: 2, mb: 1 }}>
              Select Data to Inherit
            </Typography>

            <FormGroup>
              <Controller
                name="inherit_locations"
                control={control}
                render={({ field }) => (
                  <FormControlLabel
                    control={<Checkbox {...field} checked={field.value} />}
                    label={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <LocationOn fontSize="small" color="primary" />
                        <span>Inherit Locations</span>
                        <Chip size="small" label="Recommended" color="success" variant="outlined" />
                      </Box>
                    }
                  />
                )}
              />

              <Controller
                name="inherit_vehicles"
                control={control}
                render={({ field }) => (
                  <FormControlLabel
                    control={<Checkbox {...field} checked={field.value} />}
                    label={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <DirectionsCar fontSize="small" color="primary" />
                        <span>Inherit Vehicles</span>
                        {!field.value && (
                          <Chip size="small" label="Create New" color="info" variant="outlined" />
                        )}
                      </Box>
                    }
                  />
                )}
              />

              <Controller
                name="inherit_jobs"
                control={control}
                render={({ field }) => (
                  <FormControlLabel
                    control={<Checkbox {...field} checked={field.value} />}
                    label={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Work fontSize="small" color="primary" />
                        <span>Inherit Jobs</span>
                        <Chip size="small" label="Recommended" color="success" variant="outlined" />
                      </Box>
                    }
                  />
                )}
              />
            </FormGroup>

            <Alert severity="warning">
              <Typography variant="body2">
                <strong>Typical Use Case:</strong> Inherit locations and jobs from your "base case" scenario, 
                then create new vehicles data to test different fleet configurations.
              </Typography>
            </Alert>
          </Box>
        );

      case 2:
        return (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            <Typography variant="h6">Review Configuration</Typography>

            <Card variant="outlined">
              <CardContent>
                <Typography variant="subtitle1" gutterBottom>
                  <AccountTree sx={{ mr: 1, verticalAlign: 'middle' }} />
                  New Scenario Details
                </Typography>
                <Typography variant="body2"><strong>Name:</strong> {formValues.scenario_name}</Typography>
                <Typography variant="body2"><strong>Description:</strong> {formValues.scenario_description || 'None'}</Typography>
                <Typography variant="body2"><strong>Planning Horizon:</strong> {formValues.planning_horizon_days} days</Typography>
              </CardContent>
            </Card>

            <Card variant="outlined">
              <CardContent>
                <Typography variant="subtitle1" gutterBottom>
                  <ContentCopy sx={{ mr: 1, verticalAlign: 'middle' }} />
                  Data Inheritance
                </Typography>
                <Typography variant="body2"><strong>Source:</strong> {selectedSourceScenario?.name || 'None selected'}</Typography>
                <Box sx={{ mt: 1, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  {formValues.inherit_locations && (
                    <Chip icon={<LocationOn />} label="Locations" color="success" size="small" />
                  )}
                  {formValues.inherit_vehicles && (
                    <Chip icon={<DirectionsCar />} label="Vehicles" color="success" size="small" />
                  )}
                  {formValues.inherit_jobs && (
                    <Chip icon={<Work />} label="Jobs" color="success" size="small" />
                  )}
                  {getInheritanceTypes().length === 0 && (
                    <Typography variant="caption" color="error">No data types selected for inheritance</Typography>
                  )}
                </Box>
              </CardContent>
            </Card>

            {getInheritanceTypes().length === 0 && (
              <Alert severity="error">
                You must select at least one data type to inherit from the source scenario.
              </Alert>
            )}
          </Box>
        );

      case 3:
        return (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, alignItems: 'center' }}>
            <CheckCircle color="success" sx={{ fontSize: 64 }} />
            <Typography variant="h5" align="center">
              Scenario Created Successfully!
            </Typography>
            
            {creationResult && (
              <Card variant="outlined" sx={{ width: '100%' }}>
                <CardContent>
                  <Typography variant="subtitle1" gutterBottom>
                    Creation Summary
                  </Typography>
                  <Typography variant="body2">
                    <strong>Scenario:</strong> {creationResult.scenario?.name}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Dataset:</strong> {creationResult.dataset?.name}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Inherited Data:</strong>
                  </Typography>
                  <Box sx={{ ml: 2, mt: 1 }}>
                    <Typography variant="caption">
                      • Locations: {creationResult.inheritance_result?.copied_counts?.locations || 0}
                    </Typography><br />
                    <Typography variant="caption">
                      • Vehicles: {creationResult.inheritance_result?.copied_counts?.vehicles || 0}
                    </Typography><br />
                    <Typography variant="caption">
                      • Jobs: {creationResult.inheritance_result?.copied_counts?.jobs || 0}
                    </Typography>
                  </Box>
                  <Typography variant="body2" sx={{ mt: 1 }}>
                    <strong>Execution Time:</strong> {creationResult.inheritance_result?.execution_time_ms}ms
                  </Typography>
                </CardContent>
              </Card>
            )}
          </Box>
        );

      default:
        return 'Unknown step';
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <ContentCopy color="primary" />
          Create Scenario with Data Inheritance
        </Box>
      </DialogTitle>

      <DialogContent sx={{ pb: 2 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Stepper activeStep={activeStep} sx={{ mb: 3 }}>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        <form onSubmit={handleSubmit(onSubmit)}>
          {getStepContent(activeStep)}
        </form>
      </DialogContent>

      <DialogActions>
        {activeStep === 3 ? (
          <Button onClick={handleClose} variant="contained">
            Done
          </Button>
        ) : (
          <>
            <Button onClick={handleClose}>
              Cancel
            </Button>
            <Box sx={{ flex: '1 1 auto' }} />
            <Button
              disabled={activeStep === 0}
              onClick={handleBack}
              sx={{ mr: 1 }}
            >
              Back
            </Button>
            {activeStep === steps.length - 1 ? (
              <Button
                variant="contained"
                onClick={handleSubmit(onSubmit)}
                disabled={isCreating || getInheritanceTypes().length === 0}
                sx={{ minWidth: 120 }}
              >
                {isCreating ? (
                  <CircularProgress size={20} />
                ) : (
                  'Create Scenario'
                )}
              </Button>
            ) : (
              <Button
                variant="contained"
                onClick={handleNext}
                disabled={
                  (activeStep === 0 && !formValues.scenario_name) ||
                  (activeStep === 1 && formValues.source_scenario_id === 0)
                }
              >
                Next
              </Button>
            )}
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default ScenarioInheritanceDialog;